(() => {
  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
  const SCROLL_END_DELAY_MS = 80;
  const INITIAL_SLIDE_INDEX = 1;

  class HormonizeScrollCarouselController {
    constructor(carousel, options = {}) {
      this.carousel = carousel;
      this.options = options;
      this.viewport = carousel.querySelector(options.viewportSelector);
      this.itemSelector = options.itemSelector;
      this.items = [];
      this.originalCount = 0;
      this.loopReady = false;
      this.isNormalizingLoop = false;
      this.pagination = options.paginationSelector
        ? carousel.querySelector(options.paginationSelector)
        : null;
      this.status = carousel.querySelector(options.statusSelector);
      this.prevButton = options.prevSelector ? carousel.querySelector(options.prevSelector) : null;
      this.nextButton = options.nextSelector ? carousel.querySelector(options.nextSelector) : null;
      this.activeIndex = 0;
      this.hasInitializedPosition = false;
      this.scrollEndTimer = null;
      this.scrollFrame = 0;
      this.isDragging = false;
      this.pointerStartX = 0;
      this.scrollStartLeft = 0;
      this.paginationButtons = [];
      this.mobileMediaQuery = window.matchMedia(options.mobileMediaQuery || MOBILE_MEDIA_QUERY);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScroll = this.onScroll.bind(this);
      this.onPaginationClick = this.onPaginationClick.bind(this);
      this.onViewportKeyDown = this.onViewportKeyDown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onMobileMediaQueryChange = this.onMobileMediaQueryChange.bind(this);
      this.onResize = this.onResize.bind(this);

      this.refreshItemsFromDom();
    }

    get isMobile() {
      return this.mobileMediaQuery.matches;
    }

    get isCarouselActive() {
      const count = this.loopReady ? this.originalCount : this.items.length;

      if (this.options.alwaysActive) {
        return count >= 2;
      }

      return this.isMobile && count >= 2;
    }

    get isEnabled() {
      const count = this.loopReady ? this.originalCount : this.items.length;

      if (this.options.alwaysActive) {
        return count > 0;
      }

      return this.isMobile && count > 0;
    }

    get scrollBehavior() {
      return this.reducedMotion.matches ? 'auto' : 'smooth';
    }

    get snapAlign() {
      const align = this.options.snapAlign || 'center';

      if (align === 'responsive') {
        return this.isMobile ? 'center' : 'start';
      }

      return align;
    }

    get isLoopEnabled() {
      return Boolean(this.options.loop) && this.originalCount >= 2;
    }

    get showPagination() {
      return this.options.showPagination !== false;
    }

    refreshItemsFromDom() {
      if (!this.carousel || !this.itemSelector) {
        this.items = [];
        return;
      }

      this.items = Array.from(this.carousel.querySelectorAll(this.itemSelector));
    }

    normalizeIndex(index) {
      const total = this.loopReady ? this.originalCount : this.items.length;

      if (total === 0) {
        return 0;
      }

      return ((index % total) + total) % total;
    }

    getLogicalIndex(domIndex = this.activeIndex) {
      const item = this.items[domIndex];

      if (!item) {
        return 0;
      }

      if (this.loopReady && item.dataset.loopIndex != null) {
        return Number(item.dataset.loopIndex);
      }

      return domIndex;
    }

    getOriginalDomIndex(logicalIndex) {
      if (!this.loopReady) {
        return logicalIndex;
      }

      const target = this.normalizeIndex(logicalIndex);

      return this.items.findIndex(
        (item) => item.dataset.loopClone === 'false' && Number(item.dataset.loopIndex) === target
      );
    }

    getInitialSlideIndex() {
      if (this.loopReady) {
        const offset =
          this.snapAlign === 'start' ? 0 : Math.min(this.options.initialSlideIndex ?? INITIAL_SLIDE_INDEX, this.originalCount - 1);

        return this.originalCount + offset;
      }

      if (this.snapAlign === 'start') {
        return 0;
      }

      if (this.items.length >= 2) {
        return this.options.initialSlideIndex ?? INITIAL_SLIDE_INDEX;
      }

      return 0;
    }

    getSlideTitle(item) {
      if (typeof this.options.getSlideTitle === 'function') {
        return this.options.getSlideTitle(item);
      }

      return '';
    }

    restoreLoopClones() {
      if (!this.carousel || !this.itemSelector) {
        this.loopReady = false;
        this.originalCount = 0;
        return;
      }

      this.carousel
        .querySelectorAll(`${this.itemSelector}[data-loop-clone="before"], ${this.itemSelector}[data-loop-clone="after"]`)
        .forEach((node) => node.remove());

      this.carousel.querySelectorAll(this.itemSelector).forEach((item) => {
        delete item.dataset.loopClone;
        delete item.dataset.loopIndex;
      });

      this.loopReady = false;
      this.refreshItemsFromDom();
      this.originalCount = this.items.length;
    }

    uniquifyCloneIds(root, suffix) {
      if (root.id) {
        root.id = `${root.id}-${suffix}`;
      }

      root.querySelectorAll('[id]').forEach((node) => {
        node.id = `${node.id}-${suffix}`;
      });
    }

    setupLoopClones() {
      if (!this.isLoopEnabled || this.loopReady || this.items.length < 2) {
        return;
      }

      const track = this.items[0]?.parentElement;

      if (!track) {
        return;
      }

      const originals = this.items.slice();

      originals.forEach((item, index) => {
        item.dataset.loopIndex = String(index);
        item.dataset.loopClone = 'false';
      });

      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      originals.forEach((item, index) => {
        const beforeClone = item.cloneNode(true);
        beforeClone.dataset.loopIndex = String(index);
        beforeClone.dataset.loopClone = 'before';
        beforeClone.setAttribute('aria-hidden', 'true');
        this.uniquifyCloneIds(beforeClone, `loop-before-${index}`);
        beforeFragment.appendChild(beforeClone);

        const afterClone = item.cloneNode(true);
        afterClone.dataset.loopIndex = String(index);
        afterClone.dataset.loopClone = 'after';
        afterClone.setAttribute('aria-hidden', 'true');
        this.uniquifyCloneIds(afterClone, `loop-after-${index}`);
        afterFragment.appendChild(afterClone);
      });

      track.insertBefore(beforeFragment, track.firstChild);
      track.appendChild(afterFragment);

      this.refreshItemsFromDom();
      this.loopReady = true;
    }

    normalizeLoopPosition() {
      if (!this.isLoopEnabled || !this.loopReady || this.isNormalizingLoop || !this.items.length) {
        return;
      }

      const item = this.items[this.activeIndex];

      if (!item || item.dataset.loopClone === 'false') {
        return;
      }

      const logicalIndex = Number(item.dataset.loopIndex);
      const originalDomIndex = this.getOriginalDomIndex(logicalIndex);

      if (originalDomIndex < 0 || originalDomIndex === this.activeIndex) {
        return;
      }

      this.isNormalizingLoop = true;
      this.centerSlide(originalDomIndex, { animate: false });
      this.isNormalizingLoop = false;
    }

    init() {
      this.unbindEvents();
      this.restoreLoopClones();

      if (!this.viewport || this.items.length === 0) {
        return;
      }

      this.originalCount = this.items.length;

      if (Boolean(this.options.loop) && this.originalCount >= 2) {
        this.setupLoopClones();
      }

      this.updateCarouselMode();

      if (!this.isEnabled) {
        this.clearStates();
        return;
      }

      this.buildPagination();

      if (!this.isCarouselActive) {
        this.syncSlideStates(0);
        this.updateNavButtons();
        return;
      }

      this.bindEvents();

      const initialIndex = this.getInitialSlideIndex();

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.centerSlide(initialIndex, { animate: false });
          this.hasInitializedPosition = true;
          this.updateNavButtons();
        });
      });
    }

    updateCarouselMode() {
      const count = this.loopReady ? this.originalCount : this.items.length;
      this.carousel?.classList.toggle(this.options.singleClassName || 'is-single', count === 1);
    }

    bindEvents() {
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.pagination?.addEventListener('click', this.onPaginationClick);
      this.viewport.addEventListener('keydown', this.onViewportKeyDown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.mobileMediaQuery.addEventListener('change', this.onMobileMediaQueryChange);
      window.addEventListener('resize', this.onResize);
    }

    unbindEvents() {
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.pagination?.removeEventListener('click', this.onPaginationClick);
      this.viewport?.removeEventListener('keydown', this.onViewportKeyDown);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointermove', this.onPointerMove);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.mobileMediaQuery.removeEventListener('change', this.onMobileMediaQueryChange);
      window.removeEventListener('resize', this.onResize);
    }

    buildPagination() {
      if (!this.pagination) {
        this.paginationButtons = [];
        return;
      }

      this.pagination.innerHTML = '';

      if (!this.showPagination || !this.isCarouselActive) {
        this.pagination.hidden = true;
        this.paginationButtons = [];
        return;
      }

      this.pagination.hidden = false;
      const dotClassName = this.options.dotClassName || 'hormonize-scroll-carousel__dot';
      const sourceItems = this.loopReady
        ? this.items.filter((item) => item.dataset.loopClone === 'false')
        : this.items;

      sourceItems.forEach((item, index) => {
        const dot = document.createElement('button');
        const title = this.getSlideTitle(item);
        dot.type = 'button';
        dot.className = dotClassName;
        dot.setAttribute('role', 'tab');
        dot.setAttribute(
          'aria-label',
          title ? `Show ${title}` : `${this.options.slideLabel || 'Slide'} ${index + 1}`
        );
        if (item.id) {
          dot.setAttribute('aria-controls', item.id);
        }
        dot.dataset.slideIndex = String(index);
        this.pagination.appendChild(dot);
      });

      this.paginationButtons = Array.from(this.pagination.querySelectorAll(`.${dotClassName}`));
    }

    onScroll() {
      if (!this.isCarouselActive || this.isNormalizingLoop) {
        return;
      }

      window.cancelAnimationFrame(this.scrollFrame);

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.updateActiveSlide({ announce: false });
      });

      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        this.updateActiveSlide({ announce: false });
        this.normalizeLoopPosition();
        this.updateNavButtons();
      }, this.options.scrollEndDelayMs ?? SCROLL_END_DELAY_MS);
    }

    onPaginationClick(event) {
      const button = event.target.closest('[data-slide-index]');

      if (!(button instanceof HTMLButtonElement) || !this.pagination?.contains(button)) {
        return;
      }

      const index = Number(button.dataset.slideIndex);

      if (Number.isNaN(index)) {
        return;
      }

      this.goTo(index, { userInitiated: true, logical: true });
    }

    onPrevClick() {
      if (!this.isCarouselActive) {
        return;
      }

      if (this.isLoopEnabled) {
        this.goTo(this.activeIndex - 1, { userInitiated: true });
        return;
      }

      this.goTo(Math.max(this.activeIndex - 1, 0), { userInitiated: true });
    }

    onNextClick() {
      if (!this.isCarouselActive) {
        return;
      }

      if (this.isLoopEnabled) {
        this.goTo(this.activeIndex + 1, { userInitiated: true });
        return;
      }

      this.goTo(Math.min(this.activeIndex + 1, this.items.length - 1), { userInitiated: true });
    }

    onViewportKeyDown(event) {
      if (!this.isCarouselActive) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (this.isLoopEnabled) {
          this.goTo(this.activeIndex - 1, { userInitiated: true });
        } else {
          this.goTo(Math.max(this.activeIndex - 1, 0), { userInitiated: true });
        }
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (this.isLoopEnabled) {
          this.goTo(this.activeIndex + 1, { userInitiated: true });
        } else {
          this.goTo(Math.min(this.activeIndex + 1, this.items.length - 1), { userInitiated: true });
        }
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0, { userInitiated: true, logical: true });
      }

      if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.originalCount - 1, { userInitiated: true, logical: true });
      }
    }

    onPointerDown(event) {
      if (!this.isCarouselActive || event.pointerType === 'touch') {
        return;
      }

      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      this.isDragging = true;
      this.pointerStartX = event.clientX;
      this.scrollStartLeft = this.viewport.scrollLeft;
      this.viewport.classList.add('is-dragging');
      this.viewport.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.isDragging) {
        return;
      }

      const deltaX = event.clientX - this.pointerStartX;
      this.viewport.scrollLeft = this.scrollStartLeft - deltaX;
    }

    onPointerUp(event) {
      if (!this.isDragging) {
        return;
      }

      this.isDragging = false;
      this.viewport.classList.remove('is-dragging');

      if (this.viewport.hasPointerCapture(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }

      this.updateActiveSlide({ announce: false });
      this.normalizeLoopPosition();
      this.updateNavButtons();
    }

    onMobileMediaQueryChange() {
      this.hasInitializedPosition = false;
      this.init();
    }

    onResize() {
      if (!this.isCarouselActive) {
        return;
      }

      this.centerSlide(this.activeIndex, { animate: false });
      this.updateActiveSlide({ announce: false });
      this.normalizeLoopPosition();
      this.updateNavButtons();
    }

    getClosestSlideIndex() {
      if (this.snapAlign === 'start') {
        const viewportLeft = this.viewport.scrollLeft;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.items.forEach((item, index) => {
          const distance = Math.abs(item.offsetLeft - viewportLeft);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      const viewportCenter = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const distance = Math.abs(viewportCenter - itemCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    getTargetScrollLeft(index) {
      const target = this.items[index];
      const viewportWidth = this.viewport.clientWidth;
      const maxScroll = Math.max(0, this.viewport.scrollWidth - viewportWidth);

      if (this.snapAlign === 'start') {
        return Math.max(0, Math.min(target.offsetLeft, maxScroll));
      }

      const itemWidth = target.offsetWidth;
      const targetScrollLeft = target.offsetLeft - (viewportWidth - itemWidth) / 2;
      return Math.max(0, Math.min(targetScrollLeft, maxScroll));
    }

    updateActiveSlide({ announce = false } = {}) {
      if (!this.isCarouselActive) {
        return;
      }

      const nextIndex = this.getClosestSlideIndex();

      if (nextIndex === this.activeIndex && !announce) {
        this.syncSlideStates(nextIndex);
        this.syncPagination(this.getLogicalIndex(nextIndex));
        this.updateNavButtons();
        return;
      }

      this.activeIndex = nextIndex;
      this.syncSlideStates(nextIndex);
      this.syncPagination(this.getLogicalIndex(nextIndex));
      this.updateNavButtons();

      if (announce) {
        this.announceSlide(this.getLogicalIndex(nextIndex));
      }
    }

    syncSlideStates(index) {
      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.classList.toggle('is-active', isActive);

        /* Center-orbit mode: only the focused slide is announced.
           Start-aligned multi-card strips keep siblings readable. */
        if (this.isCarouselActive && this.snapAlign === 'center') {
          item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        } else {
          item.removeAttribute('aria-hidden');
        }
      });
    }

    syncPagination(logicalIndex) {
      if (!this.paginationButtons.length) {
        return;
      }

      this.paginationButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === logicalIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    updateNavButtons() {
      const canScroll =
        this.isCarouselActive && this.viewport && this.viewport.scrollWidth > this.viewport.clientWidth + 1;

      if (this.prevButton) {
        const atStart = this.isLoopEnabled ? !canScroll : this.getLogicalIndex() <= 0 || !canScroll;
        this.prevButton.disabled = atStart;
        this.prevButton.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        this.prevButton.toggleAttribute('hidden', !this.isCarouselActive);
      }

      if (this.nextButton) {
        const atEnd = this.isLoopEnabled
          ? !canScroll
          : this.getLogicalIndex() >= this.originalCount - 1 || !canScroll;
        this.nextButton.disabled = atEnd;
        this.nextButton.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
        this.nextButton.toggleAttribute('hidden', !this.isCarouselActive);
      }
    }

    centerSlide(index, { animate = true, userInitiated = false } = {}) {
      if (!this.isCarouselActive || index < 0 || index >= this.items.length) {
        return;
      }

      const scrollLeft = this.getTargetScrollLeft(index);
      const previousSnap = this.viewport.style.scrollSnapType;
      const previousBehavior = this.viewport.style.scrollBehavior;

      if (!animate) {
        this.viewport.style.scrollSnapType = 'none';
        this.viewport.style.scrollBehavior = 'auto';
        this.viewport.scrollLeft = scrollLeft;
        void this.viewport.offsetWidth;
        this.viewport.style.scrollSnapType = previousSnap;
        this.viewport.style.scrollBehavior = previousBehavior;
      } else if (this.scrollBehavior === 'smooth') {
        this.viewport.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      } else {
        this.viewport.scrollLeft = scrollLeft;
      }

      this.activeIndex = index;
      this.syncSlideStates(index);
      this.syncPagination(this.getLogicalIndex(index));
      this.updateNavButtons();

      if (userInitiated) {
        this.announceSlide(this.getLogicalIndex(index));
      }
    }

    goTo(index, { userInitiated = false, logical = false } = {}) {
      let targetIndex = index;

      if (logical && this.loopReady) {
        const originalDomIndex = this.getOriginalDomIndex(index);
        targetIndex = originalDomIndex >= 0 ? originalDomIndex : index;
      } else if (index < 0 || index >= this.items.length) {
        return;
      }

      this.centerSlide(targetIndex, { animate: true, userInitiated });

      if (this.isLoopEnabled) {
        window.clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = window.setTimeout(() => {
          this.updateActiveSlide({ announce: false });
          this.normalizeLoopPosition();
          this.updateNavButtons();
        }, 420);
      }
    }

    announceSlide(logicalIndex) {
      if (!this.status) {
        return;
      }

      const originalDomIndex = this.getOriginalDomIndex(logicalIndex);
      const slide = this.items[originalDomIndex >= 0 ? originalDomIndex : logicalIndex];
      const title = this.getSlideTitle(slide);
      const noun = this.options.slideNoun || 'slide';
      const total = this.originalCount || this.items.length;

      this.status.textContent = title
        ? `Showing ${noun} ${logicalIndex + 1} of ${total}: ${title}`
        : `Showing ${noun} ${logicalIndex + 1} of ${total}`;
    }

    clearStates() {
      window.clearTimeout(this.scrollEndTimer);
      window.cancelAnimationFrame(this.scrollFrame);
      this.isDragging = false;
      this.hasInitializedPosition = false;
      this.isNormalizingLoop = false;
      this.viewport?.classList.remove('is-dragging');
      this.carousel?.classList.remove(this.options.singleClassName || 'is-single');

      this.items.forEach((item) => {
        item.classList.remove('is-active');
        item.removeAttribute('aria-hidden');
      });

      if (this.pagination) {
        this.pagination.innerHTML = '';
        this.pagination.hidden = !this.showPagination;
      }

      this.paginationButtons = [];
      this.activeIndex = 0;
      this.updateNavButtons();
    }

    teardown() {
      window.clearTimeout(this.scrollEndTimer);
      this.unbindEvents();
      this.clearStates();
      this.restoreLoopClones();
    }

    destroy() {
      this.teardown();
      this.carousel = null;
      this.viewport = null;
      this.items = [];
      this.pagination = null;
      this.status = null;
      this.prevButton = null;
      this.nextButton = null;
    }
  }

  window.HormonizeScrollCarouselController = HormonizeScrollCarouselController;
})();
